// src/services/paymentService.js
import api from './api';

const paymentService = {
  // Initialize Payment
  initializePayment: async (paymentData) => {
    try {
      const response = await api.post('/payments/initialize', paymentData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to initialize payment' };
    }
  },

  // Verify payment
  verifyPayment: async (verificationData) => {
    try {
      const response = await api.post('/payments/verify', verificationData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to verify payment' };
    }
  },

  // Add Funds to Wallet
  addFunds: async (depositData) => {
    try {
      const response = await api.post('/payments/add-funds', depositData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to add funds' };
    }
  },

  // Submit Manual Deposit (Ghana Only)
  submitManualDeposit: async (depositData) => {
    try {
      const response = await api.post('/payments/manual-deposit', depositData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to submit manual deposit' };
    }
  },

  // Withdraw Funds from Wallet
  withdrawFunds: async (withdrawalData) => {
    try {
      const response = await api.post('/payments/withdraw', withdrawalData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to process withdrawal' };
    }
  },

  // Get Transactions
  getTransactions: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.category) queryParams.append('category', params.category);

      const response = await api.get(`/payments/wallet/transactions?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch transactions' };
    }
  },

  // Handle Flutterwave payment
  handleFlutterwavePayment: (paymentData) => {
    return new Promise((resolve, reject) => {
      if (!window.FlutterwaveCheckout) {
        reject(new Error('Flutterwave SDK not loaded'));
        return;
      }

      window.FlutterwaveCheckout({
        public_key: paymentData.public_key || import.meta.env.VITE_FLUTTERWAVE_PUBLIC_KEY,
        tx_ref: paymentData.tx_ref,
        amount: paymentData.amount,
        currency: paymentData.currency || 'GHS',
        payment_options: 'card, mobilemoneyghana, ussd',
        customer: paymentData.customer,
        customizations: paymentData.customizations || {
          title: 'GigShip Wallet Top-Up',
          description: 'Add funds to your GigShip wallet',
          logo: '/logo.png',
        },
        callback: (response) => {
          resolve(response);
        },
        onclose: () => {
          reject(new Error('Payment cancelled'));
        }
      });
    });
  },

  // Handle PayStack payment
  handlePaystackPayment: (paymentData) => {
    return new Promise((resolve, reject) => {
      if (!window.PaystackPop) {
        reject(new Error('PayStack SDK not loaded'));
        return;
      }

      const handler = window.PaystackPop.setup({
        key: paymentData.public_key || import.meta.env.VITE_PAYSTACK_PUBLIC_KEY,
        email: paymentData.email,
        amount: paymentData.amount * 100, // PayStack expects kobo (pesewas)
        currency: paymentData.currency || 'GHS',
        ref: paymentData.reference,
        metadata: paymentData.metadata,
        callback: (response) => {
          resolve(response);
        },
        onClose: () => {
          reject(new Error('Payment cancelled'));
        }
      });

      handler.openIframe();
    });
  },

  // Admin: Get Pending Manual Deposits
  getPendingManualDeposits: async () => {
    try {
      const response = await api.get('/payments/admin/manual/pending');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch pending deposits' };
    }
  },

  // Admin: Review Manual Deposit
  reviewManualDeposit: async (id, data) => {
    try {
      const response = await api.post(`/payments/admin/manual/${encodeURIComponent(id)}/review`, data);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to review deposit' };
    }
  },

  // Admin: Get Pending Manual Methods
  getPendingManualMethods: async () => {
    try {
      const response = await api.get('/payments/admin/methods/pending');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch pending methods' };
    }
  },

  // Admin: Review Manual Method
  reviewManualMethod: async (id, data) => {
    try {
      const response = await api.post(`/payments/admin/methods/${id}/review`, data);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to review method' };
    }
  },

  // Get list of banks/networks
  getBanks: async (country = 'Ghana') => {
    try {
      const response = await api.get(`/payments/banks?country=${country}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch banks' };
    }
  },

  // Verify and Add Payment Method
  getPaymentMethods: async () => {
    try {
      const response = await api.get('/payments/methods');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch payment methods' };
    }
  },

  verifyPaymentMethod: async (methodData) => {
    try {
      const response = await api.post('/payments/methods/verify', methodData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Verification failed' };
    }
  },

  // Set a verified method as the user's default withdrawal method
  setDefaultPaymentMethod: async (methodId) => {
    try {
      const response = await api.patch(`/payments/methods/${methodId}/default`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to set default method' };
    }
  },

  // Submit for Manual Review on name mismatch
  submitManualMethodVerification: async (manualData) => {
    try {
      const response = await api.post('/payments/methods/manual', manualData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Submission failed' };
    }
  },

  // Settings: Get Public Config
  getPublicSettings: async () => {
    try {
      const response = await api.get('/settings/public');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch public settings:', error);
      return { success: false, data: {} };
    }
  },

  // Admin: Get All Settings
  getAdminSettings: async () => {
    try {
      const response = await api.get('/settings/admin');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch admin settings' };
    }
  },

  // Admin: Update Setting
  updateSetting: async (key, value) => {
    try {
      const response = await api.put(`/settings/admin/${key}`, { value });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to update setting' };
    }
  }
};

export default paymentService;